import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, ClipboardList, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RowActions from '../components/RowActions';
import { CompanyContext } from '../contexts/CompanyContext';
import { downloadEntityPdf } from '../lib/entityPdf';
import BuildingFilter from '../components/BuildingFilter';

const MANAGE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'];

export default function PPMTasks() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const [tasks, setTasks] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newTask, setNewTask] = useState({
    title: '', description: '', building_id: '', asset_id: '', frequency: 'monthly',
    due_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchData(); }, [filter]);

  const [filterBuildingId, setFilterBuildingId] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = filter !== 'all' ? `?status=${filter}` : '';
      const [t, b, a] = await Promise.all([
        axios.get(`${API}/ppm-tasks${q}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/assets`, { withCredentials: true })
      ]);
      setTasks(t.data); setBuildings(b.data); setAssets(a.data);
    } catch (e) { toast.error('Failed to load tasks'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/ppm-tasks`, {
        ...newTask,
        due_date: new Date(newTask.due_date).toISOString()
      }, { withCredentials: true });
      toast.success('PPM task created');
      setShowNew(false);
      setNewTask({ title: '', description: '', building_id: '', asset_id: '', frequency: 'monthly', due_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch { toast.error('Failed to create task'); }
  };

  // Feb-2026 workflow rule: PPM Tasks can no longer be manually flipped to
  // completed. Completion is driven by the WO→SR chain. The frontend removed
  // the "Complete" button; the backend also rejects direct status=completed
  // (see server.py update_ppm_task). Kept fetchData helper only.

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/ppm-tasks/${id}`, { withCredentials: true });
      toast.success('Task deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const downloadPpmPdf = (task) => {
    const b = buildings.find(x => x.id === task.building_id);
    const a = assets.find(x => x.id === task.asset_id);
    downloadEntityPdf({
      title: `PPM Task · ${task.title}`,
      subtitle: `${task.frequency} · ${task.status}`,
      company,
      fields: [
        { label: 'Title', value: task.title },
        { label: 'Frequency', value: task.frequency },
        { label: 'Status', value: task.status },
        { label: 'Building', value: b?.name || task.building_id },
        { label: 'Asset', value: a?.name || '—' },
        { label: 'Due date', value: new Date(task.due_date).toLocaleDateString() },
        { label: 'Completed at', value: task.completed_at ? new Date(task.completed_at).toLocaleString() : '—' },
      ],
      notes: task.description,
    });
  };

  const isOverdue = (t) => t.status === 'pending' && new Date(t.due_date) < new Date();

  return (
    <Layout>
      <div data-testid="ppm-tasks-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('ppm.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('ppm.subtitle')}</p>
          </div>
          {MANAGE_ROLES.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-ppm-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Task</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create PPM Task</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Title</Label><Input data-testid="ppm-title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} /></div>
                  <div><Label>Description</Label><Textarea value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} rows={2} /></div>
                  <div><Label>Building</Label>
                    <Select value={newTask.building_id} onValueChange={(v) => setNewTask({ ...newTask, building_id: v })}>
                      <SelectTrigger data-testid="ppm-building-select"><SelectValue placeholder="Select building" /></SelectTrigger>
                      <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Asset (optional)</Label>
                    <Select value={newTask.asset_id} onValueChange={(v) => setNewTask({ ...newTask, asset_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select asset" /></SelectTrigger>
                      <SelectContent>{assets.filter(a => !newTask.building_id || a.building_id === newTask.building_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Frequency</Label>
                      <Select value={newTask.frequency} onValueChange={(v) => setNewTask({ ...newTask, frequency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Due Date</Label><Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} /></div>
                  </div>
                  <Button data-testid="submit-ppm-btn" onClick={handleCreate} disabled={!newTask.title || !newTask.building_id} className="w-full bg-teal-600 hover:bg-teal-700">Create Task</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 flex-wrap items-center">
          {['all', 'pending', 'in_progress', 'completed'].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === s ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
              {s.replace('_', ' ')}
            </button>
          ))}
          <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} className="ml-auto" />
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid gap-3">
            {tasks.filter(t => filterBuildingId === 'all' || t.building_id === filterBuildingId).map((t) => {
              const overdue = isOverdue(t);
              return (
                <Card key={t.id} data-testid={`ppm-card-${t.id}`} className={`p-4 ${overdue ? 'border-l-4 border-l-red-500' : ''}`}>
                  <div className="flex flex-col sm:flex-row gap-3 justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{t.title}</h3>
                        {overdue && <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{t.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(t.due_date).toLocaleDateString()}</span>
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700">{t.frequency}</span>
                        <span className={`px-2 py-1 rounded-full font-semibold ${t.status === 'completed' ? 'bg-green-100 text-green-800' : t.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{t.status.replace('_', ' ')}</span>
                      </div>
                    </div>
                    {t.dispatched_wo_number && (
                      <a
                        href={`/work-orders?highlight=${t.dispatched_wo_id || ''}`}
                        data-testid={`ppm-linked-wo-${t.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 font-mono hover:bg-amber-200 transition"
                        title="Open linked Work Order"
                      >
                        ↳ {t.dispatched_wo_number}
                      </a>
                    )}
                    {t.status !== 'completed' && !t.dispatched_wo_id && MANAGE_ROLES.concat(['ENGINEER', 'CS_SUPERVISOR']).includes(user?.role) && (
                      <Button
                        data-testid={`dispatch-wo-ppm-${t.id}`}
                        size="sm"
                        variant="outline"
                        className="text-amber-600 border-amber-500 hover:bg-amber-50"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/work-orders/from-ppm/${t.id}`, { executor_type: t.service_provider_id ? 'service_provider' : 'internal' }, { withCredentials: true });
                            const { toast } = await import('sonner');
                            toast.success(`Dispatched ${r.data.wo_number}`);
                            window.location.href = `/work-orders`;
                          } catch (e) {
                            const { toast } = await import('sonner');
                            toast.error(e.response?.data?.detail || 'Dispatch failed');
                          }
                        }}
                      >
                        Dispatch WO
                      </Button>
                    )}
                    <RowActions
                      testIdPrefix={`ppm-${t.id}`}
                      onDownloadPdf={() => downloadPpmPdf(t)}
                      onDelete={MANAGE_ROLES.includes(user?.role) ? () => handleDelete(t.id) : undefined}
                      deleteLabel={`PPM task "${t.title}"`}
                    />
                  </div>
                </Card>
              );
            })}
            {tasks.length === 0 && (
              <Card className="p-10 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No PPM tasks yet</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
